import React from "react";

import {
  type FetchResult,
  type ApolloError,
  type MutationResult,
} from "@apollo/client";
import { useNorthbeamMutation } from "@hooks/http";
import {
  type DeleteConnection,
  type DeleteConnectionVariables,
  type ConnectionType,
  type UpsertConnection,
  type UpsertConnectionVariables,
} from "@north-beam/nb-client-side";

import {
  FETCH_SANITIZED_CONNECTIONS_BY_TYPE,
  UPSERT_CONNECTION,
  DELETE_CONNECTION,
} from "./queries";

interface ManageExistingConnectionProps {
  connectionType: ConnectionType;
  connectionId: string;
  children: (
    updateConnection: (
      newConnectionData: any,
    ) => Promise<
      FetchResult<UpsertConnection, Record<string, any>, Record<string, any>>
    >,
    updateInfo: MutationResult<UpsertConnection>,
    destroyConnection: () => Promise<
      FetchResult<DeleteConnection, Record<string, any>, Record<string, any>>
    >,
    destroyInfo: MutationResult<DeleteConnection>,
  ) => React.ReactNode;
}

export default function ManageExistingConnection({
  connectionType,
  connectionId,
  children,
}: ManageExistingConnectionProps) {
  const [upsertConnection, upsertInfo] = useNorthbeamMutation<
    UpsertConnection,
    UpsertConnectionVariables
  >(UPSERT_CONNECTION, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: FETCH_SANITIZED_CONNECTIONS_BY_TYPE,
        variables: { type: connectionType },
      },
    ],
  });

  const [deleteConnection, destroyInfo] = useNorthbeamMutation<
    DeleteConnection,
    DeleteConnectionVariables
  >(DELETE_CONNECTION, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: FETCH_SANITIZED_CONNECTIONS_BY_TYPE,
        variables: { type: connectionType },
      },
    ],
  });

  const updateConnection = (newConnectionData: any) => {
    return upsertConnection({
      variables: {
        id: connectionId,
        type: connectionType,
        data: newConnectionData,
      },
    });
  };

  const destroyConnection = () => {
    return deleteConnection({
      variables: {
        id: connectionId,
        type: connectionType,
      },
    });
  };

  return (
    <>
      {children(updateConnection, upsertInfo, destroyConnection, destroyInfo)}
    </>
  );
}
